import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/ui/password-input";
import { Label } from "../components/ui/label";
import { EnvelopeSimple, LockKey } from "@phosphor-icons/react";

export default function Login() {
  const { login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [failures, setFailures] = useState(0);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate("/"); else setFailures((n) => n + 1);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-background text-foreground">
      {/* Image side */}
      <div
        className="hidden lg:flex lg:col-span-3 relative items-end p-12"
        style={{
          backgroundImage:
            "linear-gradient(to top, hsl(0 0% 4% / 0.95) 0%, hsl(0 0% 4% / 0.4) 60%, transparent 100%), url('https://images.unsplash.com/photo-1601929862217-f1bf94503333?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjYXIlMjBkYXJrfGVufDB8fHx8MTc4MTM3ODAzNnww&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-lg">
          <div className="label-mono">01 Auto Group · Fleet &amp; Booking CRM</div>
          <h2 className="font-heading text-5xl mt-4 leading-[1.1]">
            Orchestrate every <span className="text-primary italic">arrival</span>,<br/>departure &amp; detail.
          </h2>
          <p className="text-muted-foreground mt-6 max-w-md">
            Unify rentals, chauffeurs and bespoke tours under one quiet, precise control room.
          </p>
        </div>
      </div>

      {/* Form side */}
      <div className="lg:col-span-2 flex items-center justify-center p-8 sm:p-12 border-l border-border">
        <div className="w-full max-w-sm">
          <div className="mb-10">
            <img src="/01-auto-group-logo.png" alt="01 Auto Group" className="w-44 block" style={{ mixBlendMode: "lighten" }} data-testid="login-logo" />
          </div>
          <div className="label-mono mb-2">Welcome back</div>
          <h1 className="font-heading text-4xl">Sign in to the console</h1>
          <p className="text-sm text-muted-foreground mt-2">Use your operator credentials.</p>

          <form onSubmit={handleSubmit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <Label className="label-mono mb-2 block">Email</Label>
              <div className="relative">
                <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="email"
                  required
                  data-testid="login-email-input"
                  className="pl-10 bg-input border-border h-11"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                />
              </div>
            </div>
            <div>
              <Label className="label-mono mb-2 block">Password</Label>
              <div className="relative">
                <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                {/* A failure re-mounts the field, which is what puts the
                    reveal back to hidden — the typed value is held here, so it
                    survives. A counter, not `error`, so two identical failures
                    still re-hide it. */}
                <PasswordInput
                  key={failures}
                  required
                  data-testid="login-password-input"
                  className="pl-10 bg-input border-border h-11"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
            </div>
            {error && <div className="text-sm text-destructive" data-testid="login-error">{error}</div>}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {loading ? "Signing in…" : "Enter the Console"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
